import React, { useEffect, useReducer, useRef } from "react";
import without from "lodash/without";
import classnames from "classnames";
import AppBanner from "components/shared/AppBanner";
import GoogleAuthButton from "components/shared/GoogleAuthButton";
import Input from "components/shared/Input";
import Select from "components/shared/Select";
import TermsCheckbox from "components/sell/TermsCheckbox";
import BirthdayInput from "components/shared/BirthdayInput";
import Checkbox from "components/shared/Checkbox";
import LcFetch from "lib/shared/fetch";

const STEP_ONE_PARAMS = ["email", "password", "country_code", "terms_checkbox"];

const STEP_TWO_PARAMS = ["username", "i_am_a", "birthday"];

const GDPR_STEP_TWO_PARAMS = [...STEP_TWO_PARAMS, "email_marketing_optin"];

const GOOGLE_STEP_TWO_PARAMS = ["i_am_a", "country_code", "birthday"];

const GOOGLE_GDPR_STEP_TWO_PARAMS = [
  ...GOOGLE_STEP_TWO_PARAMS,
  "email_marketing_optin"
];

const IGNORE_FIELDS_FOR_OAUTH = [
  "email",
  "email_confirmation",
  "password",
  "username"
];

const buildSignupParams = (state) => {
  let ignoreFields = [];
  if (state.google) {
    ignoreFields = IGNORE_FIELDS_FOR_OAUTH;
  }
  const fields = Object.entries(state.fields)
    .filter(([field, _value]) => !ignoreFields.includes(field))
    .reduce((acc, [field, value]) => {
      acc[field] = value;
      return acc;
    }, {});
  return fields;
};

const buildErrors = (errors) => {
  return errors.reduce((acc, error) => {
    [acc[error.field]] = error.message;
    return acc;
  }, {});
};

const validateStepOne = function (dispatch, state) {
  dispatch({ payload: { errors: {} }, type: "set_errors" });
  dispatch({ payload: { isSubmitting: true }, type: "set_is_submitting" });

  LcFetch(state.validatePath, {
    body: JSON.stringify({ user: state.fields }),
    method: "POST"
  })
    .then((data) => {
      data.json().then((body) => {
        const serverErrors = body.fullErrors.filter((error) =>
          isFieldVisible(state, error.field)
        );
        const fullErrors = state.fields.terms_checkbox
          ? serverErrors
          : serverErrors.concat({
              field: "terms_checkbox",
              message: ["You must accept our Terms of Service."]
            });
        if (fullErrors.length > 0) {
          dispatch({
            payload: { errors: buildErrors(fullErrors) },
            type: "set_errors"
          });
        } else {
          dispatch({
            payload: { step: 2 },
            type: "set_step"
          });
        }
      });
    })
    .finally(() => {
      dispatch({
        payload: { isSubmitting: false },
        type: "set_is_submitting"
      });
    });
};

const validateStepTwo = function (dispatch, state) {
  dispatch({ payload: { errors: {} }, type: "set_errors" });
  dispatch({ payload: { isSubmitting: true }, type: "set_is_submitting" });
  const params = buildSignupParams(state);
  LcFetch(state.validatePath, {
    body: JSON.stringify({ user: params }),
    method: "POST"
  }).then((data) => {
    data
      .json()
      .then((body) => {
        const fullErrors = body.fullErrors.filter((error) =>
          isFieldVisible(state, error.field)
        );
        if (fullErrors.length > 0) {
          dispatch({
            payload: { errors: buildErrors(fullErrors) },
            type: "set_errors"
          });
        } else {
          LcFetch(state.userRegistrationPath, {
            body: JSON.stringify({
              open_request_lit_guide_modal: state.openRequestLitGuideModal,
              signup_origin: "free_tier_modal",
              user: params,
              utm_source: state.utmSource
            }),
            method: "POST"
          }).then((response) => {
            if (response.ok) {
              window.location.href = response.headers.get("Location");
            } else {
              response.json().then((responseBody) => {
                // errors here are an object instead of an array like literally everywhere else
                const errors = Object.entries(responseBody.errors)
                  .map(([field, message]) => ({ field, message }))
                  .filter((error) => isFieldVisible(state, error.field));

                dispatch({
                  payload: { errors: buildErrors(errors) },
                  type: "set_errors"
                });
              });
            }
          });
        }
      })
      .finally(() => {
        dispatch({
          payload: { isSubmitting: false },
          type: "set_is_submitting"
        });
      });
  });
};

const initialState = (initialFields, stateOverrides) => ({
  errors: {},
  fields: {
    birthday: "",
    country_code: "",
    email: "",
    email_confirmation: "",
    email_marketing_optin: false,
    i_am_a: "",
    password: "",
    terms_checkbox: false,
    username: "",
    ...initialFields
  },
  from_gdpr_country: false,
  isSubmitting: false,
  isValid: false,
  step: 1,
  ...stateOverrides
});

const visibleFields = (state) => {
  if (state.step === 1) {
    return state.ipAddressRecognized
      ? without(STEP_ONE_PARAMS, "country_code")
      : STEP_ONE_PARAMS;
  }
  if (state.google) {
    return state.fromGdprCountry ||
      (!state.ipAddressRecognized && !state.fields.country_code)
      ? GOOGLE_GDPR_STEP_TWO_PARAMS
      : GOOGLE_STEP_TWO_PARAMS;
  }

  if (state.fromGdprCountry || !state.ipAddressRecognized) {
    return GDPR_STEP_TWO_PARAMS;
  }
  return STEP_TWO_PARAMS;
};

const isFieldVisible = (state, field) => {
  if (field === "base") {
    // base errors should always be visible
    return true;
  }
  return visibleFields(state).includes(field);
};

const setFields = (state, fields = {}) => {
  return { ...state, fields: { ...state.fields, ...fields } };
};

function reducer(state, action) {
  switch (action.type) {
    case "set_errors":
      return { ...state, errors: action.payload.errors };
    case "set_fields":
      return setFields(state, action.payload.fields);
    case "set_country_code":
      return {
        ...state,
        fields: {
          ...state.fields,
          country_code: action.payload.country_code.value,
          email_marketing_optin: !action.payload.country_code.conforms_to_gdpr
        },
        fromGdprCountry: action.payload.country_code.conforms_to_gdpr
      };
    case "set_step":
      return { ...state, step: action.payload.step };
    case "set_is_submitting":
      return { ...state, isSubmitting: action.payload.isSubmitting };
    case "set_google_step":
      return { ...state, google: true, step: 2 };
    case "reset_modal":
      return {
        ...state,
        errors: {},
        fields: {
          country_code: state.fields.country_code
        },
        step: 1
      };
    default:
      return state;
  }
}

function ErrorList(props) {
  const { errors } = props;

  if (Object.keys(errors).length === 0) {
    return null;
  }

  return (
    <div className="modal__errors lc-banner lc-banner-warning lc-banner-section">
      <ul>
        {Object.entries(errors).map(([field, message]) => (
          <li key={field}>{message}</li>
        ))}
      </ul>
    </div>
  );
}

function RegistrationModal(props) {
  const {
    country_codes: countryCodes,
    from_gdpr_country: fromGdprCountry,
    i_am_a_options: iAmAOptions,
    ip_address_recognized: ipAddressRecognized,
    ip_country_code: countryCode,
    open_request_lit_guide_modal: openRequestLitGuideModal,
    privacy_policy_url: privacyPolicyUrl,
    signin_path: signinPath,
    step_2_title: step2Title,
    subtitle,
    terms_path: termsPath,
    title,
    user_registration_path: userRegistrationPath,
    utm_source: utmSource,
    validate_path: validatePath
  } = props;
  const [state, dispatch] = useReducer(
    reducer,
    initialState(
      {
        country_code: countryCode
      },
      {
        fromGdprCountry,
        ipAddressRecognized,
        openRequestLitGuideModal,
        userRegistrationPath,
        utmSource,
        validatePath
      }
    )
  );

  const modalRef = useRef(null);

  useEffect(() => {
    const onOauthRegistrationInProgress = () => {
      dispatch({ type: "set_google_step" });
    };
    const onHiddenBsModal = () => {
      dispatch({ type: "reset_modal" });
    };
    $(modalRef.current).on(
      "oauthRegistrationInProgress",
      onOauthRegistrationInProgress
    );
    $(".registration-modal.modal").on("hidden.bs.modal", onHiddenBsModal);
    return () => {
      $(modalRef.current).off(
        "oauthRegistrationInProgress",
        onOauthRegistrationInProgress
      );
      $(".registration-modal.modal").off("hidden.bs.modal", onHiddenBsModal);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (state.step === 1) {
      validateStepOne(dispatch, state);
    } else {
      validateStepTwo(dispatch, state);
    }
  };
  return (
    <form noValidate onSubmit={handleSubmit} ref={modalRef}>
      {state.step === 1 && (
        <div className="registration-modal__step-1">
          <div className="modal__titles">
            <div className="modal__title">{title}</div>
            <div className="modal__subtitle">{subtitle}</div>
          </div>
          <AppBanner />
          <GoogleAuthButton
            openRequestLitGuideModal={openRequestLitGuideModal}
            registrationInProgressEvent={{
              type: "oauthRegistrationInProgress"
            }}
          />
          <p className="horizontal-separator subtitle-2">or</p>
          <ErrorList errors={state.errors} />

          <div className="modal__form-group">
            <Input
              name="user[email]"
              label="Email"
              inputClass="registration-modal__email input-md"
              labelText="Email"
              value={state.fields.email}
              hasError={!!state.errors.email}
              isControlled
              handleChange={(e) =>
                dispatch({
                  payload: {
                    fields: {
                      email: e.target.value,
                      email_confirmation: e.target.value
                    }
                  },
                  type: "set_fields"
                })
              }
              inputId="user_email"
            />
          </div>
          <div className="modal__form-group">
            <Input
              name="user[password]"
              label="Password"
              inputClass="registration-modal__password input-md"
              labelText="Password (at least 8 characters)"
              value={state.fields.password}
              hasError={!!state.errors.password}
              isControlled
              handleChange={(e) =>
                dispatch({
                  payload: { fields: { password: e.target.value } },
                  type: "set_fields"
                })
              }
              inputId="user_password"
              type="password"
            />
          </div>
          {visibleFields(state).includes("country_code") && (
            <div className="modal__form-group registration-modal__country-select-block">
              <Select
                name="user[country_code]"
                label="Country"
                inputClass="registration-modal__country_code input-md"
                value={state.fields.country_code}
                isControlled
                hasError={!!state.errors.country_code}
                onChange={(option) =>
                  dispatch({
                    payload: { country_code: option },
                    type: "set_country_code"
                  })
                }
                id="user_country_code"
                type="select"
                options={countryCodes}
                placeholder="Select one"
                labelText="Country"
              />
            </div>
          )}
          <div
            className={classnames(
              "modal__form-group registration-modal__terms",
              { error: !!state.errors.terms_checkbox }
            )}
          >
            <TermsCheckbox
              name="user[terms_checkbox]"
              url={termsPath}
              privacyPolicyUrl={privacyPolicyUrl}
              checked={state.fields.terms_checkbox}
              onChange={() =>
                dispatch({
                  payload: {
                    fields: { terms_checkbox: !state.fields.terms_checkbox }
                  },
                  type: "set_fields"
                })
              }
            />
          </div>
          <div className="registration-modal__footer">
            <span>
              Already have an account?{" "}
              <a href={signinPath} data-amplitude="freeTierModalSignIn">
                Sign in
              </a>
            </span>
            <button
              className="btn btn-primary btn-lg registration-modal__next-step"
              type="submit"
              disabled={state.isSubmitting}
            >
              Continue
            </button>
          </div>
        </div>
      )}
      {state.step === 2 && (
        <div className="registration-modal__step-2">
          <div className="modal__titles">
            <div className="modal__title">{step2Title}</div>
          </div>
          <ErrorList errors={state.errors} />
          {visibleFields(state).includes("country_code") && (
            <div className="modal__form-group registration-modal__country-select-block">
              <Select
                name="user[country_code]"
                label="Country"
                inputClass="registration-modal__country_code input-md"
                value={state.fields.country_code}
                isControlled
                hasError={!!state.errors.country_code}
                onChange={(option) =>
                  dispatch({
                    payload: { country_code: option },
                    type: "set_country_code"
                  })
                }
                id="user_country_code"
                type="select"
                options={countryCodes}
                placeholder="Select one"
                labelText="Country"
              />
            </div>
          )}
          {visibleFields(state).includes("username") && (
            <div className="modal__form-group">
              <Input
                name="user[username]"
                label="Username"
                inputClass="registration-modal__username input-md"
                labelText="Username"
                value={state.fields.username}
                hasError={!!state.errors.username}
                isControlled
                handleChange={(e) =>
                  dispatch({
                    payload: { fields: { username: e.target.value } },
                    type: "set_fields"
                  })
                }
                inputId="user_username"
              />
            </div>
          )}
          <div className="modal__form-group">
            <Select
              name="user[i_am_a]"
              label="I am a"
              inputClass="registration-modal__i_am_a input-md"
              value={state.fields.i_am_a}
              hasError={!!state.errors.i_am_a}
              isControlled
              onChange={(option) =>
                dispatch({
                  payload: { fields: { i_am_a: option.value } },
                  type: "set_fields"
                })
              }
              id="user_i_am_a"
              type="select"
              options={iAmAOptions}
              placeholder="Select one"
              labelText="I am a"
            />
          </div>
          {visibleFields(state).includes("birthday") && (
            <div className="modal__form-group registration-modal__birthday">
              <BirthdayInput
                name="user[birthday]"
                labelText="Birthday"
                inputClass="input-md input-md"
                value={state.fields.birthday}
                hasError={!!state.errors.birthday}
                isControlled
                handleChange={(e) =>
                  dispatch({
                    payload: { fields: { birthday: e.target.value } },
                    type: "set_fields"
                  })
                }
                inputId="user_birthday"
                infoMessage="Please enter as MM/DD/YYYY"
              />
            </div>
          )}
          <div
            className={classnames(
              "modal__form-group checkbox registration-modal__marketing-opt-in",
              { hidden: !isFieldVisible(state, "email_marketing_optin") }
            )}
          >
            <Checkbox
              name="user[email_marketing_optin]"
              label="Get email updates when we add new guides and features. Unsubscribe at any time."
              value={state.fields.email_marketing_optin}
              hasError={!!state.errors.email_marketing_optin}
              isControlled
              onChange={(e) =>
                dispatch({
                  payload: {
                    fields: { email_marketing_optin: e.target.checked }
                  },
                  type: "set_fields"
                })
              }
            />
          </div>
          <div className="registration-modal__footer">
            <span>
              Already have an account?{" "}
              <a href={signinPath} data-amplitude="freeTierModalSignIn">
                Sign in
              </a>
            </span>
            <button
              className="btn btn-primary btn-lg registration-modal__create-account"
              type="submit"
              disabled={state.isSubmitting}
            >
              Create Account
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

export default RegistrationModal;
